import { MaterialUnit } from "fe-shared/src/types/domain";
import React, { useMemo } from "react";

import {
  TransferDetailsLocationsSection,
  FlagComments,
  TransferContentSection,
} from "..";
import {
  Transfer,
  TransferType,
  TransactionSection,
  TransactionFlag,
} from "../../types";

interface Props {
  transfer: Transfer;
  isStaffView: boolean;
  canEditTransfer?: boolean;
}

const TRANSFER_CONFIG = {
  [TransferType.IntoProcessing]: {
    unit: MaterialUnit.Kg,
    isSourceWarehouse: true,
    isWithDocuments: true,
    isWithNonEligibles: false,
  },
  [TransferType.OutOfProcessing]: {
    unit: MaterialUnit.Kg,
    isSourceWarehouse: false,
    isWithDocuments: true,
    isWithNonEligibles: false,
  },
  [TransferType.NonEligibleWriteOff]: {
    unit: MaterialUnit.Kg,
    isSourceWarehouse: true,
    isWithDocuments: true,
    isWithNonEligibles: false,
  },
  [TransferType.NonEligibleBufferWriteOff]: {
    unit: MaterialUnit.Kg,
    isSourceWarehouse: true,
    isWithDocuments: true,
    isWithNonEligibles: true,
  },
  [TransferType.PickupFromStorage]: {
    unit: MaterialUnit.Units,
    isSourceWarehouse: true,
    isWithDocuments: false,
    isWithNonEligibles: false,
  },
  [TransferType.DeliveryToStorage]: {
    unit: MaterialUnit.Units,
    isSourceWarehouse: false,
    isWithDocuments: false,
    isWithNonEligibles: false,
  },
};

const TransferContentView = ({
  transfer,
  isStaffView,
  canEditTransfer,
}: Props) => {
  const {
    sourceWarehouse,
    targetWarehouse,
    transferType,
    transferFlags = [],
    nonEligibleMaterial,
  } = transfer;
  const visibleTransferFlags = isStaffView
    ? transferFlags
    : transferFlags.filter((item) => item.isVisibleToParticipant);

  const flagsBySection = useMemo(
    () =>
      Object.values(TransactionSection).reduce((result, s) => {
        result[s] = visibleTransferFlags.filter((f) => f.sectionCode === s);
        return result;
      }, {} as { [key in TransactionSection]: TransactionFlag[] }),
    [visibleTransferFlags]
  );

  const getFlagComments = (sectionCode: TransactionSection) => (
    <FlagComments flags={flagsBySection[sectionCode]} />
  );

  const {
    unit,
    isSourceWarehouse,
    isWithDocuments,
    isWithNonEligibles,
  } = TRANSFER_CONFIG[transferType];
  const storageWarehouse = isSourceWarehouse
    ? sourceWarehouse
    : targetWarehouse;

  return (
    <>
      <TransferDetailsLocationsSection
        transfer={transfer}
        isStaffView={isStaffView}
        flags={getFlagComments(TransactionSection.Locations)}
      />
      <TransferContentSection
        unit={unit}
        transfer={transfer}
        isStaffView={isStaffView}
        isWithDocuments={isWithDocuments}
        canEditTransfer={canEditTransfer}
        isWithNonEligibles={isWithNonEligibles}
        location={storageWarehouse?.location}
        isActive={storageWarehouse?.isActive}
        materialName={nonEligibleMaterial?.materialName}
      />
      {getFlagComments(TransactionSection.Data)}
    </>
  );
};

export default TransferContentView;
